import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export interface UrlTranslation {
  page_key: string;
  en_slug: string;
  de_slug: string | null;
  es_slug: string | null;
  fr_slug: string | null;
  pt_slug: string | null;
  ru_slug: string | null;
  it_slug: string | null;
  sv_slug: string | null;
  zh_slug: string | null;
}

/**
 * Hook to fetch all URL translations for routing
 */
export function useUrlTranslationsData() {
  return useQuery({
    queryKey: ['url-translations'],
    queryFn: async (): Promise<UrlTranslation[]> => {
      const { data, error } = await supabase
        .from('url_translations')
        .select('*')
        .order('page_key');

      if (error) {
        console.error('Error fetching URL translations:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });
}

/**
 * Get the localized slug for a page key and language
 */
export function getLocalizedSlug(
  translations: UrlTranslation[],
  pageKey: string,
  language: string
): string {
  const translation = translations.find(t => t.page_key === pageKey);
  if (!translation) return `/${pageKey}`;

  const langKey = `${language}_slug` as keyof UrlTranslation;
  const slug = translation[langKey] as string | null;
  
  return slug || translation.en_slug;
}

/**
 * Get the page key from a localized slug
 */
export function getPageKeyFromSlug(
  translations: UrlTranslation[],
  slug: string,
  language: string
): string | null {
  const langKey = `${language}_slug` as keyof UrlTranslation;
  
  for (const translation of translations) {
    const localizedSlug = translation[langKey] as string | null;
    if (localizedSlug === slug || translation.en_slug === slug) {
      return translation.page_key;
    }
  }
  
  return null;
}

/**
 * Hook to get localized URL functions
 */
export function useLocalizedUrls() {
  const { language } = useLanguage();
  const { data: translations = [] } = useUrlTranslationsData();

  const getUrl = (pageKey: string): string => {
    const slug = getLocalizedSlug(translations, pageKey, language);
    
    // For default language (en), no prefix needed
    if (language === 'en') {
      return slug;
    }
    
    return `/${language}${slug}`;
  };

  const getPageKey = (slug: string): string | null => {
    return getPageKeyFromSlug(translations, slug, language);
  };

  return {
    getUrl,
    getPageKey,
    translations,
    isLoading: translations.length === 0,
  };
}
